import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, RotateCcw, Printer } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = {
  paper_size: "A4", orientation: "portrait",
  margin_top: 6, margin_right: 7, margin_bottom: 6, margin_left: 7,
  show_header: true, content_scale: 100,
};

/** Prescription-card layout preferences — margins, paper size, hide header. */
export default function PrescriptionCardSettings() {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/hospital/settings/prescription-card");
      setForm({ ...DEFAULTS, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load prescription-card settings");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setNum = (k, v) => set(k, v === "" ? "" : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        margin_top: Number(form.margin_top) || 0,
        margin_right: Number(form.margin_right) || 0,
        margin_bottom: Number(form.margin_bottom) || 0,
        margin_left: Number(form.margin_left) || 0,
        content_scale: Number(form.content_scale) || 100,
      };
      const { data } = await api.patch("/hospital/settings/prescription-card", payload);
      setForm({ ...DEFAULTS, ...data });
      toast.success("Prescription-card settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => setForm(DEFAULTS);

  return (
    <Card className="p-4" data-testid="rx-card-settings">
      <div className="mb-3">
        <div className="text-sm font-semibold text-ink">Prescription / OPD card layout</div>
        <div className="text-xs text-ink-muted">
          Fine-tune paper size, margins and the printed header so the card fits your stationery.
          Applied to every doctor&apos;s paper Rx print (<span className="font-mono">/print/prescription-card/…</span>).
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>Paper size</Label>
          <Select value={form.paper_size} onValueChange={(v) => set("paper_size", v)}>
            <SelectTrigger data-testid="rx-paper-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
              <SelectItem value="A5">A5 (148 × 210 mm)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Orientation</Label>
          <Select value={form.orientation} onValueChange={(v) => set("orientation", v)}>
            <SelectTrigger data-testid="rx-orientation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="portrait">Portrait</SelectItem>
              <SelectItem value="landscape">Landscape</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <Label>Top margin (mm)</Label>
          <Input type="number" min="0" step="0.5" value={form.margin_top} onChange={(e) => setNum("margin_top", e.target.value)} data-testid="rx-margin-top" />
        </div>
        <div>
          <Label>Right margin (mm)</Label>
          <Input type="number" min="0" step="0.5" value={form.margin_right} onChange={(e) => setNum("margin_right", e.target.value)} data-testid="rx-margin-right" />
        </div>
        <div>
          <Label>Bottom margin (mm)</Label>
          <Input type="number" min="0" step="0.5" value={form.margin_bottom} onChange={(e) => setNum("margin_bottom", e.target.value)} data-testid="rx-margin-bottom" />
        </div>
        <div>
          <Label>Left margin (mm)</Label>
          <Input type="number" min="0" step="0.5" value={form.margin_left} onChange={(e) => setNum("margin_left", e.target.value)} data-testid="rx-margin-left" />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex items-center gap-2">
          <Switch checked={form.show_header} onCheckedChange={(v) => set("show_header", v)} data-testid="rx-show-header" />
          <Label className="cursor-pointer" onClick={() => set("show_header", !form.show_header)}>
            Show hospital header
            <span className="ml-1 text-[11px] text-ink-muted">(uncheck to leave the top fully blank — for pre-printed letterhead)</span>
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Label>Content scale (%)</Label>
          <Input type="number" min="70" max="130" step="5" className="w-20" value={form.content_scale} onChange={(e) => setNum("content_scale", e.target.value)} data-testid="rx-content-scale" />
          <span className="text-[11px] text-ink-muted">70–130. Shrink to fit more sections.</span>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="rx-card-save">
          <Save size={14} className="mr-2" />Save
        </Button>
        <Button variant="outline" onClick={resetDefaults} data-testid="rx-card-reset">
          <RotateCcw size={14} className="mr-2" />Reset to defaults
        </Button>
        <a
          href="/app/patients"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs text-ink hover:bg-slate-50"
          data-testid="rx-card-preview-link"
          title="Open a patient and print their Rx card to preview these settings"
        >
          <Printer size={12} />How to preview
        </a>
      </div>
      <div className="mt-1 text-[11px] text-ink-muted">
        To preview: open any patient → click <b>Print prescription card</b>. The saved settings apply immediately.
      </div>

      <LivePreview settings={form} />
    </Card>
  );
}

/* --------------------------------------------------------------------------
 * Live preview panel
 * A scaled-down A4/A5 mock-up that redraws in real time as the admin tweaks
 * paper size / margins / header / scale. We use `transform: scale(...)` on a
 * full-size .rx-preview-page so the mm units render identically to the actual
 * PrintPrescriptionCard — no separate CSS to keep in sync.
 * ------------------------------------------------------------------------ */
function LivePreview({ settings }) {
  const isLandscape = settings.orientation === "landscape";
  const widthMm  = settings.paper_size === "A5" ? (isLandscape ? 210 : 148) : (isLandscape ? 297 : 210);
  const heightMm = settings.paper_size === "A5" ? (isLandscape ? 148 : 210) : (isLandscape ? 210 : 297);
  // Shrink the full page down to fit within the settings panel (~340px wide).
  const targetWidth = 340;
  const mmToPx = 3.7795275591; // 1mm in CSS px
  const zoom = targetWidth / (widthMm * mmToPx);
  const scale = Math.max(70, Math.min(130, Number(settings.content_scale) || 100)) / 100;

  return (
    <div className="mt-6 border-t border-border pt-4" data-testid="rx-card-live-preview">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-ink">Live preview</div>
          <div className="text-[11px] text-ink-muted">
            {settings.paper_size} · {settings.orientation} · Margins T{settings.margin_top} R{settings.margin_right} B{settings.margin_bottom} L{settings.margin_left} mm · {settings.content_scale}% scale
          </div>
        </div>
        <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-ink-muted">
          {widthMm} × {heightMm} mm
        </span>
      </div>

      <div className="flex justify-center rounded-md border border-dashed border-border bg-slate-50 p-4">
        <div style={{
          width: `${widthMm * mmToPx * zoom}px`,
          height: `${heightMm * mmToPx * zoom}px`,
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            width: `${widthMm}mm`,
            height: `${heightMm}mm`,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            background: "white",
            boxShadow: "0 1px 4px rgba(0,0,0,.08)",
            padding: `${settings.margin_top}mm ${settings.margin_right}mm ${settings.margin_bottom}mm ${settings.margin_left}mm`,
            boxSizing: "border-box",
            fontFamily: "'Calibri','Carlito','Helvetica','Arial',sans-serif",
            color: "#0f172a",
          }}>
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` }}>
              {settings.show_header ? (
                <div style={{ display: "flex", alignItems: "center", gap: "5mm", borderBottom: "1.2px solid #0e3b6e", paddingBottom: "1.5mm", marginBottom: "2mm" }}>
                  <div>
                    <div style={{ fontSize: "15pt", fontWeight: 700, color: "#0e3b6e" }}>Hospital Name</div>
                    <div style={{ fontSize: "8pt", color: "#475569", marginTop: "0.3mm" }}>Address · Phone · Reg No</div>
                  </div>
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <div style={{ fontSize: "8.5pt", color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>OPD Prescription Card</div>
                    <div style={{ fontSize: "8.5pt", color: "#64748b" }}>Date · Time</div>
                  </div>
                </div>
              ) : null}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5mm 4mm", border: "0.6px solid #cbd5e1", background: "#f8fafc", padding: "1.5mm 2.5mm", borderRadius: "1.2mm", fontSize: "9pt" }}>
                {["UHID","Name","Age / Sex","Mobile","Doctor","Dept","Address","—"].map((k, i) => (
                  <div key={i} style={{ gridColumn: k === "Address" ? "span 2" : undefined }}>
                    <div style={{ fontSize: "6.5pt", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{k}</div>
                    <div style={{ fontWeight: 600, fontSize: "9pt" }}>—</div>
                  </div>
                ))}
              </div>

              {["History","Visual Acuity","Refraction","Examination","Diagnosis & Plan","Prescription (Rx)"].map((title) => (
                <div key={title} style={{ marginTop: "2mm" }}>
                  <div style={{ fontSize: "9pt", fontWeight: 800, color: "#0e3b6e", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #0e3b6e", paddingBottom: "0.4mm", marginBottom: "1mm" }}>{title}</div>
                  <div style={{
                    minHeight: title === "Prescription (Rx)" ? "35mm" : "12mm",
                    background: "repeating-linear-gradient(to bottom, transparent 0, transparent 7mm, #dbe2ea 7mm, #dbe2ea 7.12mm)",
                  }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 text-center text-[10px] text-ink-muted">
        Scaled to fit — actual print will match the exact mm values above.
      </div>
    </div>
  );
}
